/**
 * Distribucion de la ventana Diferido que abre la Causacion al elegir una cuenta diferida.
 *
 * Sigue el mockup aprobado. Lo que esta distribucion hace cumplir por si sola:
 * - El diferido NO pide tercero (Mantis #350): se abre por la cuenta diferida y el parametro
 *   «maneja diferidos».
 * - Fecha de registro, dias y periodos los calcula la ventana: se leen deshabilitados.
 * - La fecha inicial solo ofrece desde la fecha del sistema hasta el fin del ano siguiente, y la
 *   final, dias posteriores a la inicial. La regla sigue en sus validadores.
 * - La cuenta destino se elige en su buscador y su nombre solo se lee.
 * - La distribucion por centro de costo es la grilla editable del ERP: el motor le reserva el
 *   sitio y la ventana la llena.
 */
import { Signal } from '@angular/core';
import { NebulaLayoutBranch, NebulaLayoutHelper, NebulaSelectOption } from 'nebula-ui-kit';

const I18N = 'ACCOUNTING.DIFERIDO_DIALOG';

/** Que hace el boton que abre un buscador; se deriva de la firma del ayudante. */
type AccionDeCampo = Parameters<NebulaLayoutHelper['search']>[2];

/** Lo que la ventana ya decidio y la distribucion solo coloca. */
export interface EstadoDelDiferidoDialogo {
  bases: NebulaSelectOption[];
  buscarCuenta: AccionDeCampo;
  minInicio: Signal<Date | null>;
  maxInicio: Signal<Date | null>;
  minFin: Signal<Date | null>;
}

/**
 * Arma la distribucion de la ventana.
 *
 * @param e lo que la ventana resuelve y la distribucion solo coloca.
 * @returns la raiz de la distribucion.
 */
export function distribucionDelDiferidoDialogo(e: EstadoDelDiferidoDialogo): NebulaLayoutBranch {
  const l = new NebulaLayoutHelper();
  const derivado = { disabled: true };

  return l.form(
    'diferido-dialogo',
    {},
    l.section(
      'valor-periodo',
      `${I18N}.SECTION.VALUE_PERIOD`,
      { icon: 'CalendarClock', template: 'repeat(2, minmax(0, 1fr))' },
      l.number('valorDiferido', `${I18N}.VALUE`, {
        placeholderKey: `${I18N}.VALUE`,
        tooltipKey: `${I18N}.TOOLTIP.VALUE`,
        required: true,
      }),
      l.text('fechaRegistro', `${I18N}.REGISTER_DATE`, {
        ...derivado,
        tooltipKey: `${I18N}.TOOLTIP.REGISTER_DATE`,
      }),
      l.date('fechaInicial', `${I18N}.START_DATE`, {
        tooltipKey: `${I18N}.TOOLTIP.START_DATE`,
        minDate: e.minInicio,
        maxDate: e.maxInicio,
        required: true,
      }),
      l.date('fechaFinal', `${I18N}.END_DATE`, {
        tooltipKey: `${I18N}.TOOLTIP.END_DATE`,
        minDate: e.minFin,
        required: true,
      }),
      l.text('diasDiferidos', `${I18N}.DAYS`, { ...derivado, tooltipKey: `${I18N}.TOOLTIP.DAYS` }),
      l.text('periodosDiferir', `${I18N}.PERIODS`, {
        ...derivado,
        tooltipKey: `${I18N}.TOOLTIP.PERIODS`,
      }),
      l.segmented('baseCalculo', `${I18N}.BASE`, e.bases, {
        tooltipKey: `${I18N}.TOOLTIP.BASE`,
        required: true,
        span: 2,
      }),
    ),
    l.section(
      'cuenta-destino',
      `${I18N}.SECTION.ACCOUNT`,
      { icon: 'BookOpen', template: '160px minmax(0, 1fr)' },
      // La cuenta en UN control: su codigo, su nombre pegado y la lupa que la busca.
      l.code('codCuenta', `${I18N}.ACCOUNT_CODE`, 'nombreCuenta', {
        accion: e.buscarCuenta,
        placeholderKey: 'COLUMNS.CODE',
        tooltipKey: `${I18N}.TOOLTIP.ACCOUNT_CODE`,
        adjunto: { campo: 'nombreCuenta', labelKey: `${I18N}.ACCOUNT_NAME` },
        span: 2,
        required: true,
      }),
    ),
    l.zone('centros', `${I18N}.COST_CENTERS`, 'centrosDelDiferido', { chrome: 'bare' }),
  );
}
